/**
 * Desktop shell — sidebar + header, with the page rendered into <Outlet/>.
 *
 * The phone has a 5-slot bottom tab bar plus a "More" hub holding the other
 * eleven modules. Desktop has no slot pressure, so one Menu carries every
 * destination and the More page stops existing here — it was only ever a
 * workaround for five slots. (It stays on the phone build untouched.)
 */
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar, Badge, Button, Drawer, Dropdown, Flex, Layout, Menu, Popover, Tooltip, Typography,
} from 'antd';
import {
  ApartmentOutlined, BankOutlined, BarChartOutlined, BellOutlined, CalendarOutlined, ContactsOutlined, DownloadOutlined,
  FileTextOutlined, FormOutlined, GiftOutlined, HomeOutlined, LineChartOutlined, LogoutOutlined, MailOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, MoonOutlined, ReadOutlined, RightOutlined, SettingOutlined, ShopOutlined,
  PlaySquareOutlined, SunOutlined, SwapOutlined, ToolOutlined, UserOutlined, NotificationOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { WHATS_NEW_PATH } from '../lib/releaseNotes';
import { TUTORIALS_PATH } from '../lib/tutorials';
import { useUnreadCount } from '../components/NotifBell';
import useMemoUnread from '../hooks/useMemoUnread';
import { useTour } from '../learn/TourContext';
import ListingAssistant from '../components/ListingAssistant';
import { viewAs } from '../api/client';
import NotifPanel from './NotifPanel';
import { inits } from './ui';
import * as coShareApi from '../api/coShare';

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

/**
 * The four-point sparkle — the mark that reads as "this thinks" across every
 * product that has shipped an assistant. A thunderbolt (what this button used
 * to wear) reads as speed or as a power feature, not as an AI one.
 *
 * Two stars rather than one: a lone sparkle is easily read as a favourite or a
 * rating, and the second, smaller one is what makes the pair unmistakable.
 *
 * The GLYPH carries the gradient, not a plate behind it. Its neighbours in that
 * row are bare outline icons and a round avatar, so a filled rounded square sat
 * among them as a different species — heavier than everything else and the only
 * corner in the set. Colouring the mark itself keeps it the most interesting
 * thing up there without making it the biggest.
 */
const SparkIcon = (props) => (
  <span role="img" className="anticon" {...props}>
    <svg width="1em" height="1em" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id="cid-spark-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--navy)" />
          <stop offset="55%" stopColor="var(--primary)" />
          <stop offset="100%" stopColor="var(--accent)" />
        </linearGradient>
      </defs>
      {/* Concave-sided star: each quadratic pulls the edge back to the centre. */}
      <path fill="url(#cid-spark-grad)" d="M10 4.2 Q11 11 17.8 12 Q11 13 10 19.8 Q9 13 2.2 12 Q9 11 10 4.2 Z" />
      <path fill="url(#cid-spark-grad)" opacity=".75" d="M18.4 2.6 Q18.9 6.1 22.4 6.6 Q18.9 7.1 18.4 10.6 Q17.9 7.1 14.4 6.6 Q17.9 6.1 18.4 2.6 Z" />
    </svg>
  </span>
);

/**
 * `tour` is the Learn Mode anchor (see src/learn/). The sidebar IS the desktop's
 * navigation lesson: the phone teaches "tap the tab, then find it under More",
 * and every one of those steps lands here instead — so the walkthrough needs a
 * name for each destination it points at.
 */
const NAV = [
  { type: 'group', label: 'WORKSPACE' },
  { key: '/', icon: <HomeOutlined />, label: 'Home', tour: 'nav-home' },
  { key: '/sales', icon: <FileTextOutlined />, label: 'Sales', tour: 'nav-sales' },
  { key: '/listings', icon: <ShopOutlined />, label: 'Listings', tour: 'nav-listings' },
  { key: '/earnings', icon: <BarChartOutlined />, label: 'Earnings', tour: 'nav-earnings' },
  { key: '/events', icon: <CalendarOutlined />, label: 'Events' },
  { key: '/memos', icon: <NotificationOutlined />, label: 'Memos', badge: 'memos' },
  { type: 'group', label: 'PAPERWORK' },
  { key: '/forms', icon: <FormOutlined />, label: 'Booking forms', tour: 'nav-forms' },
  { key: '/letters', icon: <MailOutlined />, label: 'Co-agency letters', tour: 'nav-letters' },
  { key: '/co-share', icon: <SwapOutlined />, label: 'Co-share', badge: 'coShare', tour: 'nav-co-share' },
  { key: '/contacts', icon: <ContactsOutlined />, label: 'Contacts', tour: 'nav-contacts' },
  { type: 'group', label: 'PEOPLE & TOOLS' },
  { key: '/team', icon: <ApartmentOutlined />, label: 'Team & career', tour: 'nav-team' },
  { key: '/team/performance', icon: <LineChartOutlined />, label: 'Team performance', scoped: true },
  { key: '/tools', icon: <ToolOutlined />, label: 'Tools', tour: 'nav-tools' },
  // Project materials: brochures, price lists, photos and videos from HQ.
  { key: '/projects', icon: <BankOutlined />, label: 'Projects' },
  { key: '/downloads', icon: <DownloadOutlined />, label: 'CID Forms', tour: 'nav-downloads' },
  // Learn Mode. The phone reaches it from the More hub; desktop has no More, so
  // it sits at the foot of the nav — and in the avatar menu, which is where a
  // "teach me this app" affordance is looked for on a desktop product.
  { key: '/learn', icon: <ReadOutlined />, label: 'Learn the app', tour: 'nav-learn' },
  // How-it-works videos (lib/tutorials.js); also in the avatar menu.
  { key: TUTORIALS_PATH, icon: <PlaySquareOutlined />, label: 'Tutorials' },
];

export default function DesktopShell({ perfAny }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { agent, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const unread = useUnreadCount();
  const memosUnread = useMemoUnread();
  /* Learn Mode rebuilds a screen when the lesson steps BACK into a block it can
     only replay from the start — /forms/new is always a fresh form, so landing
     mid-way through the create flow would show a step over a form whose contents
     no longer exist. Keying the outlet on the counter is what makes that rebuild
     happen; the phone build does the same on its full-screen route group. */
  const { remountKey } = useTour() || {};
  const [collapsed, setCollapsed] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Co-share requests waiting on this agent — the same probe the More tab runs.
  const [coShareDue, setCoShareDue] = useState(0);
  useEffect(() => {
    coShareApi.getCoShareSummary()
      .then((r) => setCoShareDue(Number((r?.data ?? r)?.actionable) || 0))
      .catch(() => {});
  }, []);

  // Longest matching key wins, so /team/performance does not light up /team.
  const selected = useMemo(() => {
    const keys = NAV.filter((n) => n.key).map((n) => n.key);
    // A branch sale is opened from the Sales page, so it lights up Sales.
    const path = pathname.startsWith('/branch-sales') ? '/sales' : pathname;
    const hit = keys
      .filter((k) => (k === '/' ? path === '/' : path.startsWith(k)))
      .sort((a, b) => b.length - a.length)[0];
    return hit ? [hit] : [];
  }, [pathname]);

  // Collapsed, the rail is 64px wide — a group heading has nowhere to render, and
  // antd does not drop it, it TRUNCATES it: "WORKSPACE" became "WOR…", stacked
  // above the icons as three lines of noise. The grouping still earns its keep,
  // so it survives as a rule instead of a word. The first heading needs none —
  // the brand bar's own border already separates it.
  let seenGroup = false;
  const items = NAV
    // Team performance stays out of the nav entirely until the server confirms
    // the caller holds a scope — an entry that only ever leads to an empty
    // state is worse than no entry.
    .filter((n) => !n.scoped || perfAny)
    .map((n, i) => {
      if (n.type === 'group') {
        const first = !seenGroup;
        seenGroup = true;
        if (!collapsed) return { type: 'group', key: 'g' + i, label: n.label };
        return first ? null : { type: 'divider', key: 'g' + i };
      }
      // A count beside the label: co-share requests waiting on this agent, memos from HQ not yet read.
      const count = n.badge === 'coShare' ? coShareDue : n.badge === 'memos' ? memosUnread : 0;
      const due = count > 0;
      return {
        key: n.key,
        // Reaches the <li> — rc-menu spreads unknown props onto the row, so the
        // Learn Mode spotlight lands on the whole nav item and not just its text.
        'data-tour': n.tour,
        // Collapsed there is no label to hang the count beside, so it rides ON
        // the icon — otherwise an unanswered co-share request is invisible until
        // the rail is opened again.
        //
        // Offset because the default corner lands the badge 6px ABOVE the tile,
        // and `.ant-menu-item` is `overflow: hidden` — so the top of the number
        // was being sliced off. Pushed down and right to sit fully inside.
        icon: due && collapsed
          ? <Badge count={count} size="small" offset={[4, 7]}>{n.icon}</Badge>
          : n.icon,
        label: due
          ? <Flex align="center" gap={8}><span style={{ flex: 1 }}>{n.label}</span><Badge count={count} size="small" /></Flex>
          : n.label,
      };
    })
    .filter(Boolean);

  const ren = 'REN ' + String(agent?.ren_no || '').replace(/^REN\s*/i, '');

  return (
    // `cid-shell` is what Learn Mode's ribbon shrinks: while a lesson runs #root
    // is pinned below a 34px strip, and a shell fixed at 100vh would push its own
    // footer that far off the bottom of the screen. See design.css.
    <Layout className="cid-shell" style={{ height: '100vh' }}>
      <Sider theme="light" width={250} collapsedWidth={64} collapsed={collapsed}
        style={{ borderRight: '1px solid var(--border)' }}>
        <div className="cid-brand">
          {/* The real mark — the app installs under it, so the shell wears it. */}
          <div className={collapsed ? 'cid-logo mark' : 'cid-logo'}>
            <img src="/cid_fulllogo.png" alt="CID Realtors" />
          </div>
        </div>
        <div className="cid-scroll" style={{ flex: 1, overflowY: 'auto', paddingTop: 8 }}>
          <Menu mode="inline" inlineCollapsed={collapsed} selectedKeys={selected} items={items}
            onClick={({ key }) => nav(key)} style={{ borderInlineEnd: 0, fontWeight: 600 }} />
        </div>
        <div className="cid-sbfoot" onClick={() => nav('/profile')}>
          <Avatar size={32} style={{ background: 'var(--navy)', fontSize: 11.5, fontWeight: 700, flex: 'none' }}>
            {inits(agent?.full_name)}
          </Avatar>
          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 650 }} className="trunc">{agent?.full_name || '—'}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>{ren} · {agent?.gci_tier}% tier</Text>
              </div>
              <RightOutlined style={{ fontSize: 11, color: 'var(--muted)' }} />
            </>
          )}
        </div>
      </Sider>

      <Layout>
        <Header style={{ display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <Button type="text" className="cid-hdrbtn" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)} />
          {/* No global search: every list carries its own, scoped to what it holds. */}
          <span style={{ flex: 1 }} />
          {/* Same bare shape and 40px footprint as the bell and the theme toggle —
              the colour lives in the mark, so this reads as the liveliest icon in
              the row rather than as a block dropped into it. */}
          {/* Not while HQ views as the agent: the Copilot answers as the agent and
              refuses everything in a read-only view. */}
          {!viewAs.active && (
            <Tooltip title="CID Copilot AI">
              <Button
                type="text"
                className="cid-hdrbtn cid-ai"
                icon={<SparkIcon />}
                onClick={() => setAiOpen(true)}
                aria-label="Open CID Copilot AI"
              />
            </Tooltip>
          )}
          <Popover open={notifOpen} onOpenChange={setNotifOpen} trigger="click" placement="bottomRight"
            destroyOnHidden content={<NotifPanel onClose={() => setNotifOpen(false)} />}
            styles={{ body: { padding: 12 } }}>
            {/* Badge sits on the wrapper's corner by default, which on a 40px
                button leaves it floating clear of a centred 18px glyph — pulled
                back onto the bell itself. */}
            <Badge count={unread} size="small" offset={[-9, 9]}>
              <Button type="text" className="cid-hdrbtn" icon={<BellOutlined />} />
            </Badge>
          </Popover>
          <Tooltip title={dark ? 'Light mode' : 'Dark mode'}>
            <Button type="text" className="cid-hdrbtn" icon={dark ? <SunOutlined /> : <MoonOutlined />} onClick={toggle} />
          </Tooltip>
          <Dropdown menu={{
            items: [
              { key: 'p', icon: <UserOutlined />, label: 'Profile & settings', onClick: () => nav('/profile') },
              { key: 's', icon: <SettingOutlined />, label: 'Preferences', onClick: () => nav('/profile') },
              { key: 'l', icon: <ReadOutlined />, label: 'Learn the app', onClick: () => nav('/learn') },
              { key: 't', icon: <PlaySquareOutlined />, label: 'Tutorials', onClick: () => nav(TUTORIALS_PATH) },
              { key: 'w', icon: <GiftOutlined />, label: <span>What&rsquo;s new <Text type="secondary" style={{ fontSize: 11 }}>v{__APP_VERSION__}</Text></span>, onClick: () => nav(WHATS_NEW_PATH) },
              { type: 'divider' },
              { key: 'o', icon: <LogoutOutlined />, label: 'Sign out', danger: true, onClick: logout },
            ],
          }}>
            <Avatar size={30} style={{ background: 'var(--navy)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              {inits(agent?.full_name)}
            </Avatar>
          </Dropdown>
        </Header>

        <Content id="cid-scroll" className="cid-scroll" style={{ overflowY: 'auto' }}>
          <Outlet key={remountKey} />
        </Content>
      </Layout>

      {/* The assistant is a full-screen rising sheet on the phone. On desktop a
          chat is a companion to the page you are on — you ask it about what you
          are looking at — so it arrives from the end edge and leaves the page
          visible behind it. Same component, rendered bare inside the Drawer. */}
      <Drawer
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        placement="right"
        width={460}
        closable={false}
        title={null}
        destroyOnHidden={false}
        styles={{ body: { padding: 0, background: 'var(--bg)' }, header: { display: 'none' } }}
      >
        <ListingAssistant embedded onClose={() => setAiOpen(false)} />
      </Drawer>
    </Layout>
  );
}
